/**
 * useCaptchaFieldSync — bridges the Turnstile captcha token (which lives
 * outside the contact form-state machine, mounted via `<CaptchaField slot>`)
 * into the form's `captchaToken` field so the validator + submit envelope
 * pick it up.
 *
 * Depends on the stable `setField` + the token only — depending on the full
 * form object would re-fire on every state mutation; this keeps the sync
 * idempotent.
 *
 * Byte-mirror of the landed hooks of the same name in port-web, palm-web and
 * prep-web. Making the bridge a custom hook (rather than a raw `useEffect`
 * sitting after the form-factory `useMemo`) is what lets `<ContactPage>`
 * satisfy `web/component-state-order`: the effect's real dependency —
 * `setField`, which only exists once `useHook()` has run — is invisible to the
 * linter because it is a plain property read, not a hook destructure, so the
 * rule read the effect as freely movable above the computed hooks. It is not.
 *
 * @module portals/marketing-website/hooks/useCaptchaFieldSync
 */

import { useEffect } from 'react';

/**
 * @param {(name: string, value: string) => void} setField - The form's
 *   stable field setter (`form.setField` from createUseContactForm).
 * @param {string|null} captchaToken - Current captcha token (or null).
 * @returns {void}
 */
export function useCaptchaFieldSync(setField, captchaToken) {
  useEffect(() => {
    setField('captchaToken', captchaToken || '');
  }, [captchaToken, setField]);
}

export default useCaptchaFieldSync;
